/**
 * A small cron matcher for scheduled triggers — "the attraction runs Saturdays
 * from seven".
 *
 * Standard five fields (minute hour day-of-month month day-of-week) with `*`,
 * numbers, `a-b` ranges, `a,b` lists and `/n` steps. Deliberately not a full
 * cron implementation: no names, no `@weekly`, no `L`/`W`. Anything it cannot
 * parse throws at publish time rather than failing silently at showtime.
 */
export interface CalendarParts {
  minute: number
  hour: number
  dayOfMonth: number
  month: number
  /** 0 = Sunday. */
  dayOfWeek: number
}

interface FieldRange {
  min: number
  max: number
}

const FIELD_RANGES: FieldRange[] = [
  { min: 0, max: 59 }, // minute
  { min: 0, max: 23 }, // hour
  { min: 1, max: 31 }, // day of month
  { min: 1, max: 12 }, // month
  { min: 0, max: 6 }, // day of week
]

export type CronMatcher = (parts: CalendarParts) => boolean

function parseField(field: string, range: FieldRange, expression: string): Set<number> {
  const allowed = new Set<number>()

  for (const term of field.split(',')) {
    const [spec, stepText] = term.split('/')
    const step = stepText === undefined ? 1 : Number(stepText)
    if (!Number.isInteger(step) || step < 1) {
      throw new Error(`Cron expression "${expression}" has an invalid step in "${term}".`)
    }

    let from: number
    let to: number
    if (spec === '*') {
      from = range.min
      to = range.max
    } else if (spec.includes('-')) {
      const [a, b] = spec.split('-').map(Number)
      from = a
      to = b
    } else {
      from = Number(spec)
      to = Number(spec)
    }

    if (!Number.isInteger(from) || !Number.isInteger(to) || from > to) {
      throw new Error(`Cron expression "${expression}" has an invalid range in "${term}".`)
    }
    if (from < range.min || to > range.max) {
      throw new Error(
        `Cron expression "${expression}" uses ${from}-${to}, outside the allowed ${range.min}-${range.max}.`,
      )
    }

    for (let value = from; value <= to; value += step) allowed.add(value)
  }

  return allowed
}

export function compileCron(expression: string): CronMatcher {
  const fields = expression.trim().split(/\s+/)
  if (fields.length !== 5) {
    throw new Error(
      `Cron expression "${expression}" needs five fields (minute hour day month weekday), got ${fields.length}.`,
    )
  }

  const [minute, hour, dayOfMonth, month, dayOfWeek] = fields.map((field, i) =>
    parseField(field, FIELD_RANGES[i], expression),
  )

  const dayOfMonthRestricted = fields[2] !== '*'
  const dayOfWeekRestricted = fields[4] !== '*'

  return (parts) => {
    if (!minute.has(parts.minute)) return false
    if (!hour.has(parts.hour)) return false
    if (!month.has(parts.month)) return false

    // Cron's long-standing quirk: when both day fields are restricted, either
    // matching is enough.
    const dayMatches = dayOfMonth.has(parts.dayOfMonth)
    const weekdayMatches = dayOfWeek.has(parts.dayOfWeek)
    if (dayOfMonthRestricted && dayOfWeekRestricted) return dayMatches || weekdayMatches
    if (dayOfMonthRestricted) return dayMatches
    if (dayOfWeekRestricted) return weekdayMatches
    return true
  }
}

const WEEKDAY_INDEX: Record<string, number> = {
  Sun: 0,
  Mon: 1,
  Tue: 2,
  Wed: 3,
  Thu: 4,
  Fri: 5,
  Sat: 6,
}

/**
 * Calendar parts for an instant in a named zone. Worlds keep local hours — a
 * Halloween attraction opens at seven in its own town, not in UTC — so the
 * schedule is read through the placement's timezone.
 */
export function calendarPartsInZone(epochMs: number, timeZone: string): CalendarParts {
  const formatter = new Intl.DateTimeFormat('en-US', {
    timeZone,
    hour12: false,
    year: 'numeric',
    month: 'numeric',
    day: 'numeric',
    hour: 'numeric',
    minute: 'numeric',
    weekday: 'short',
  })

  const parts: Record<string, string> = {}
  for (const { type, value } of formatter.formatToParts(new Date(epochMs))) {
    parts[type] = value
  }

  return {
    minute: Number(parts.minute),
    // Some locales render midnight as 24; normalise it.
    hour: Number(parts.hour) % 24,
    dayOfMonth: Number(parts.day),
    month: Number(parts.month),
    dayOfWeek: WEEKDAY_INDEX[parts.weekday ?? 'Sun'] ?? 0,
  }
}
